import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import AdminInviteService from '../services/AdminInviteService';
import ProjectInvitation from '../types/invitation/ProjectInvitation';
import ProjectInvitationParticipant from '../types/invitation/ProjectInvitationParticipant';
import UploadState from '../../types/UploadState';
import ProjectInvitationRecap from '../types/invitation/ProjectInvitationRecap';

const NAME = 'AdminInvite';

const createInvite = createAsyncThunk(
    `${NAME}/createInvite`,
    AdminInviteService.createInvite,
);

const getInvites = createAsyncThunk(
    `${NAME}/getInvites`,
    AdminInviteService.getInvites,
);

const getInvite = createAsyncThunk(
    `${NAME}/getInvite`,
    AdminInviteService.getInvite,
);
const saveInvite = createAsyncThunk(
    `${NAME}/saveInvite`,
    AdminInviteService.saveInvite,
);
const getParticipants = createAsyncThunk(
    `${NAME}/getParticipants`,
    AdminInviteService.getParticipants,
);
const changeParticipantLanguage = createAsyncThunk(
    `${NAME}/changeParticipantLanguage`,
    AdminInviteService.changeParticipantLanguage,
);
const selectParticipant = createAsyncThunk(
    `${NAME}/selectParticipant`,
    AdminInviteService.selectParticipant,
);
const selectParticipants = createAsyncThunk(
    `${NAME}/selectParticipants`,
    AdminInviteService.selectParticipants,
);
const deselectParticipant = createAsyncThunk(
    `${NAME}/deselectParticipant`,
    AdminInviteService.deselectParticipant,
);
const deselectParticipants = createAsyncThunk(
    `${NAME}/deselectParticipants`,
    AdminInviteService.deselectParticipants,
);
const changeName = createAsyncThunk(
    `${NAME}/changeName`,
    AdminInviteService.changeName,
);
const getRecap = createAsyncThunk(
    `${NAME}/getRecaps`,
    AdminInviteService.getRecaps,
);
const setMultilingual = createAsyncThunk(
    `${NAME}/setMultilingual`,
    AdminInviteService.setMultilingual,
);
const sendInvite = createAsyncThunk(
    `${NAME}/sendInvite`,
    AdminInviteService.sendInvite,
);

export type AdminInviteState = {
    invites: IBaseState<ProjectInvitation[]>;
    detail: IBaseState<ProjectInvitation | null>;
    participants: UploadState<ProjectInvitationParticipant[] | null>;
    recap: IBaseState<ProjectInvitationRecap[]>;
};

const initialState: AdminInviteState = {
    invites: { data: [], loading: false },
    detail: { data: null, loading: false },
    participants: { data: null, loading: false, uploading: false },
    recap: { data: [] },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            createInvite.fulfilled,
            (state: AdminInviteState, action) => {
                state.invites.data.push(action.payload.data);
            },
        );

        builder.addCase(
            getParticipants.fulfilled,
            (state: AdminInviteState, action) => {
                state.participants.data = action.payload.data;
            },
        );

        builder.addCase(getInvites.pending, (state: AdminInviteState) => {
            state.invites.loading = true;
        });
        builder.addCase(
            getInvites.fulfilled,
            (state: AdminInviteState, action) => {
                state.invites.data = action.payload.data;
            },
        );

        builder.addCase(
            changeParticipantLanguage.fulfilled,
            (state: AdminInviteState, action) => {
                if (state.participants.data) {
                    const participantIndex = state.participants.data.findIndex(
                        (item: ProjectInvitationParticipant) =>
                            item.id === action.meta.arg.participantId,
                    );
                    if (participantIndex >= 0 && action.payload?.data?.locale) {
                        state.participants.data[participantIndex].locale =
                            action.payload.data.locale;
                    }
                }
            },
        );

        builder.addCase(
            selectParticipant.fulfilled,
            (state: AdminInviteState, action) => {
                if (state.participants.data) {
                    state.participants.data.push(action.payload.data);
                }
            },
        );

        builder.addCase(
            selectParticipants.fulfilled,
            (state: AdminInviteState, action) => {
                state.participants.data = action.payload.data;
            },
        );

        builder.addCase(
            deselectParticipant.fulfilled,
            (state: AdminInviteState, action) => {
                if (state.participants.data) {
                    const participantIndex = state.participants.data.findIndex(
                        (participant) =>
                            participant.id === action.meta.arg.participantId,
                    );

                    if (participantIndex >= 0) {
                        state.participants.data.splice(participantIndex, 1);
                    }
                }
            },
        );

        builder.addCase(
            deselectParticipants.fulfilled,
            (state: AdminInviteState) => {
                state.participants.data = [];
            },
        );

        builder.addCase(getRecap.pending, (state: AdminInviteState) => {
            state.recap.loading = true;
        });
        builder.addCase(
            getRecap.fulfilled,
            (state: AdminInviteState, action) => {
                state.recap.data = action.payload.data;
            },
        );

        builder.addMatcher(
            isAnyOf(
                selectParticipant.pending,
                selectParticipants.pending,
                deselectParticipant.pending,
                deselectParticipants.pending,
                changeParticipantLanguage.pending,
            ),
            (state: AdminInviteState) => {
                state.participants.uploading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                selectParticipant.fulfilled,
                selectParticipant.rejected,
                selectParticipants.fulfilled,
                selectParticipants.rejected,
                deselectParticipant.fulfilled,
                deselectParticipant.rejected,
                deselectParticipants.fulfilled,
                deselectParticipants.rejected,
                changeParticipantLanguage.fulfilled,
                changeParticipantLanguage.rejected,
            ),
            (state: AdminInviteState) => {
                state.participants.uploading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getParticipants.pending),
            (state: AdminInviteState) => {
                state.participants.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(getParticipants.fulfilled, getParticipants.rejected),
            (state: AdminInviteState) => {
                state.participants.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getInvites.fulfilled, getInvites.rejected),
            (state: AdminInviteState) => {
                state.invites.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getRecap.pending, sendInvite.pending),
            (state: AdminInviteState) => {
                state.recap.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getRecap.fulfilled,
                getRecap.rejected,
                sendInvite.fulfilled,
                sendInvite.rejected,
            ),
            (state: AdminInviteState) => {
                state.recap.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createInvite.pending,
                getInvite.pending,
                saveInvite.pending,
                setMultilingual.pending,
            ),
            (state: AdminInviteState) => {
                state.detail.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                createInvite.fulfilled,
                createInvite.rejected,
                getInvite.fulfilled,
                getInvite.rejected,
                saveInvite.fulfilled,
                saveInvite.rejected,
                setMultilingual.fulfilled,
                setMultilingual.rejected,
            ),
            (state: AdminInviteState) => {
                state.detail.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getInvite.fulfilled,
                saveInvite.fulfilled,
                setMultilingual.fulfilled,
            ),
            (state: AdminInviteState, action) => {
                state.detail.data = action.payload.data;
            },
        );
    },
});

const state = (state: AppState) => state.admin.invite;

const AdminInviteSlice = {
    reducer: slice.reducer,
    actions: {
        createInvite,
        getInvite,
        getInvites,
        getParticipants,
        selectParticipant,
        selectParticipants,
        deselectParticipant,
        deselectParticipants,
        changeParticipantLanguage,
        saveInvite,
        changeName,
        getRecap,
        setMultilingual,
        sendInvite,
    },
    state: {
        getAll: createSelector(state, (state: AdminInviteState) => state),
        getDetail: createSelector(
            state,
            (state: AdminInviteState) => state.detail,
        ),
        getParticipants: createSelector(
            state,
            (state: AdminInviteState) => state.participants,
        ),
        getInvites: createSelector(
            state,
            (state: AdminInviteState) => state.invites,
        ),
        getRecap: createSelector(
            state,
            (state: AdminInviteState) => state.recap,
        ),
    },
};

export default AdminInviteSlice;
